import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
    Box, Typography, Stack, Button, TextField, Chip, Alert, CircularProgress, Container, Link, Grid, IconButton, Tooltip,
} from '@mui/material';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import WorkOutlineRoundedIcon from '@mui/icons-material/WorkOutlineRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import {
    publicCareersVacancies, publicCareersVacancy, publicCareersApply, publicCareersTrack,
} from '../Api/Api';
import { PRIMARY, PRIMARY_LIGHT, PRIMARY_BORDER } from '../theme';
import { solidBtn, ghostBtn, field, LoadAlert } from '../components/uiKit';

// Plain axios on purpose: these calls are public and must never carry the HRMS token.
const msgOf = (err, fallback) => err?.response?.data?.message || fallback;

const SRC_MAP = { linkedin: 'LinkedIn', naukri: 'Naukri' };
const RESUME_OK = /\.(pdf|docx?)$/i;

function Shell({ children, title, careersName }) {
    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>
            <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #E6EAF1' }}>
                <Container maxWidth="md" sx={{ py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                    <Link component={RouterLink} to={`/careers/${careersName}`} underline="none" sx={{ color: '#0F172A' }}>
                        <Typography sx={{ fontSize: 19, fontWeight: 800 }}>{title || 'Careers'}</Typography>
                    </Link>
                    <Link component={RouterLink} to={`/careers/${careersName}/track`} sx={{ fontSize: 13.5, fontWeight: 700, color: PRIMARY }}>
                        Track your application
                    </Link>
                </Container>
            </Box>
            <Container maxWidth="md" sx={{ py: { xs: 2.5, sm: 4 } }}>{children}</Container>
        </Box>
    );
}

function Facts({ v }) {
    const items = [
        [PlaceRoundedIcon, v.location],
        [WorkOutlineRoundedIcon, v.employmentType],
        [ScheduleRoundedIcon, v.experienceRange],
        [PaymentsRoundedIcon, v.salary],
    ].filter(([, t]) => t);
    return (
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1.6, mt: 0.8 }}>
            {items.map(([Icon, t]) => (
                <Stack key={t} direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                    <Icon sx={{ fontSize: 16, color: '#94A3B8' }} />
                    <Typography sx={{ fontSize: 13, color: '#64748B' }}>{t}</Typography>
                </Stack>
            ))}
        </Stack>
    );
}

export function CareersHome() {
    const { careersName } = useParams();
    const [state, setState] = useState({ loading: true, error: '', data: null });

    const load = useCallback(async () => {
        setState((s) => ({ ...s, loading: true, error: '' }));
        try {
            const { data: body } = await axios.get(publicCareersVacancies(careersName));
            if (body?.error) throw new Error(body.message);
            setState({ loading: false, error: '', data: body.data });
        } catch (err) {
            setState({ loading: false, error: msgOf(err, 'We could not load the open roles.'), data: null });
        }
    }, [careersName]);
    useEffect(() => { load(); }, [load]);

    const { loading, error, data } = state;
    const list = data?.vacancies || [];
    const [params] = useSearchParams();
    const src = params.get('src');

    return (
        <Shell title={data?.company} careersName={careersName}>
            <Typography sx={{ fontSize: { xs: 24, sm: 30 }, fontWeight: 800, color: '#0F172A' }}>
                {data?.company ? `Careers at ${data.company}` : 'Careers'}
            </Typography>
            <Typography sx={{ fontSize: 14.5, color: '#64748B', mt: 0.5, mb: 3 }}>Open roles</Typography>

            {loading ? (
                <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress sx={{ color: PRIMARY }} /></Box>
            ) : error ? (
                <LoadAlert action={<Button size="small" onClick={load}>Retry</Button>}>{error}</LoadAlert>
            ) : list.length === 0 ? (
                <Box sx={{ p: 5, textAlign: 'center', bgcolor: '#fff', border: '1px solid #E6EAF1', borderRadius: '12px' }}>
                    <Typography sx={{ color: '#64748B' }}>There are no open roles right now. Please check back soon.</Typography>
                </Box>
            ) : (
                <Stack spacing={1.5}>
                    {list.map((v) => (
                        <Box key={v.publicKey} component={RouterLink}
                            to={`/careers/${careersName}/${v.publicKey}${src ? `?src=${encodeURIComponent(src)}` : ''}`}
                            sx={{
                                display: 'block', textDecoration: 'none', bgcolor: '#fff', border: '1px solid #E6EAF1', borderRadius: '12px', p: 2.2,
                                transition: 'border-color .15s, box-shadow .15s', '&:hover': { borderColor: PRIMARY_BORDER, boxShadow: '0 10px 24px -14px rgba(124,92,252,0.5)' },
                            }}>
                            <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ fontSize: 17, fontWeight: 800, color: '#0F172A' }}>{v.title}</Typography>
                                    {(v.department || v.designation) && (
                                        <Typography sx={{ fontSize: 12.5, color: '#98A0AE' }}>{[v.department, v.designation].filter(Boolean).join(' · ')}</Typography>
                                    )}
                                </Box>
                                {v.openings > 0 && <Chip size="small" label={`${v.openings} opening${v.openings === 1 ? '' : 's'}`} sx={{ bgcolor: PRIMARY_LIGHT, color: PRIMARY, fontWeight: 700 }} />}
                            </Stack>
                            <Facts v={v} />
                            {v.postedOn && <Typography sx={{ fontSize: 12, color: '#98A0AE', mt: 1 }}>Posted {v.postedOn}</Typography>}
                        </Box>
                    ))}
                </Stack>
            )}
        </Shell>
    );
}

export function CareersJob() {
    const { careersName, publicKey } = useParams();
    const [params] = useSearchParams();
    const source = SRC_MAP[(params.get('src') || '').toLowerCase()] || 'Career Page';

    const [state, setState] = useState({ loading: true, closed: false, error: '', company: '', vacancy: null });
    const [applying, setApplying] = useState(false);
    const [done, setDone] = useState(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data: body } = await axios.get(publicCareersVacancy(careersName, publicKey));
                if (alive) setState({ loading: false, closed: false, error: '', company: body.data.company, vacancy: body.data.vacancy });
            } catch (err) {
                const closed = err?.response?.status === 404;
                if (alive) setState({ loading: false, closed, error: closed ? '' : msgOf(err, 'We could not load this role.'), company: '', vacancy: null });
            }
        })();
        return () => { alive = false; };
    }, [careersName, publicKey]);

    const { loading, closed, error, company, vacancy: v } = state;

    return (
        <Shell title={company} careersName={careersName}>
            <Button component={RouterLink} to={`/careers/${careersName}`} startIcon={<ArrowBackRoundedIcon />} sx={{ ...ghostBtn, mb: 2, height: 34 }}>All roles</Button>

            {loading ? (
                <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress sx={{ color: PRIMARY }} /></Box>
            ) : closed ? (
                <Box sx={{ p: 5, textAlign: 'center', bgcolor: '#fff', border: '1px solid #E6EAF1', borderRadius: '12px' }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0F172A' }}>This role is no longer open</Typography>
                    <Link component={RouterLink} to={`/careers/${careersName}`} sx={{ display: 'inline-block', mt: 1.5, fontWeight: 700 }}>See other open roles</Link>
                </Box>
            ) : error ? (
                <Alert severity="error">{error}</Alert>
            ) : done ? (
                <Box sx={{ p: { xs: 2.5, sm: 4 }, bgcolor: '#fff', border: '1px solid #E6EAF1', borderRadius: '12px' }}>
                    <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#0F172A' }}>Thank you — your application has been received.</Typography>
                    {done.trackingCode && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: PRIMARY_LIGHT, border: `1px solid ${PRIMARY_BORDER}`, borderRadius: '10px' }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#6E6B99', textTransform: 'uppercase' }}>Your application ID</Typography>
                            <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ fontSize: 22, fontWeight: 800, color: PRIMARY }}>{done.trackingCode}</Typography>
                                <Tooltip title="Copy"><IconButton onClick={() => navigator.clipboard?.writeText(done.trackingCode)}><ContentCopyRoundedIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                            </Stack>
                            <Typography sx={{ fontSize: 13, color: '#475569' }}>
                                Keep this ID. You need it together with your email to check your status.
                            </Typography>
                        </Box>
                    )}
                    <Stack direction="row" spacing={1.2} sx={{ mt: 2.5, flexWrap: 'wrap', gap: 1 }}>
                        <Button component={RouterLink} to={`/careers/${careersName}/track`} sx={{ ...solidBtn, px: 2.2 }}>Track your application</Button>
                        <Button component={RouterLink} to={`/careers/${careersName}`} sx={{ ...ghostBtn, px: 2.2 }}>Back to careers</Button>
                    </Stack>
                </Box>
            ) : (
                <>
                    <Box sx={{ p: { xs: 2.2, sm: 3 }, bgcolor: '#fff', border: '1px solid #E6EAF1', borderRadius: '12px', mb: 2 }}>
                        <Typography sx={{ fontSize: { xs: 22, sm: 26 }, fontWeight: 800, color: '#0F172A' }}>{v.title}</Typography>
                        <Typography sx={{ fontSize: 13, color: '#98A0AE' }}>{[v.department, v.designation].filter(Boolean).join(' · ')}</Typography>
                        <Facts v={v} />
                        <Stack direction="row" spacing={1} sx={{ mt: 1.2, flexWrap: 'wrap', gap: 0.8 }}>
                            {v.openings > 0 && <Chip size="small" label={`${v.openings} opening${v.openings === 1 ? '' : 's'}`} sx={{ bgcolor: PRIMARY_LIGHT, color: PRIMARY, fontWeight: 700 }} />}
                            {v.postedOn && <Chip size="small" variant="outlined" label={`Posted ${v.postedOn}`} />}
                        </Stack>
                        {v.description && (
                            <Typography sx={{ fontSize: 14.5, color: '#334155', lineHeight: 1.7, mt: 2, whiteSpace: 'pre-line' }}>{v.description}</Typography>
                        )}
                        {!applying && <Button onClick={() => setApplying(true)} sx={{ ...solidBtn, mt: 2.5, height: 42, px: 3 }}>Apply now</Button>}
                    </Box>
                    {applying && <ApplyForm careersName={careersName} publicKey={publicKey} source={source} onDone={setDone} />}
                </>
            )}
        </Shell>
    );
}

const EMPTY = { fullName: '', email: '', phone: '', currentCompany: '', experienceYears: '', expectedSalary: '', noticePeriod: '' };

function ApplyForm({ careersName, publicKey, source, onDone }) {
    const [form, setForm] = useState(EMPTY);
    const [resume, setResume] = useState(null);
    const [resumeErr, setResumeErr] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');
    const [tried, setTried] = useState(false);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const emailBad = !/^\S+@\S+\.\S+$/.test(form.email.trim());
    const nameBad = !form.fullName.trim();
    const phoneDigits = form.phone.replace(/\D/g, '');
    const phoneBad = phoneDigits.length < 10 || phoneDigits.length > 15;
    const resumeMissing = !resume;
    const salaryBad = form.expectedSalary !== '' && Number(form.expectedSalary) < 0;
    const expNum = Number(form.experienceYears);
    const expBad = form.experienceYears !== '' && (!(expNum >= 0 && expNum <= 60));
    const noticeBad = form.noticePeriod.length >= 30;

    const pick = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!RESUME_OK.test(f.name)) { setResume(null); setResumeErr('Upload a .pdf, .doc or .docx file.'); return; }
        if (f.size > 10 * 1024 * 1024) { setResume(null); setResumeErr('The file must be 10 MB or smaller.'); return; }
        setResumeErr(''); setResume(f);
    };

    const submit = async (e) => {
        e.preventDefault();
        setTried(true);
        if (nameBad || emailBad || phoneBad || expBad || noticeBad || salaryBad || resumeMissing || resumeErr || sending) return;
        const fd = new FormData();
        Object.entries(form).forEach(([k, val]) => { if (String(val).trim() !== '') fd.append(k, String(val).trim()); });
        fd.append('source', source);
        if (resume) fd.append('resume', resume);
        // The `website` honeypot is deliberately never appended.
        setSending(true); setError('');
        try {
            const { data: body } = await axios.post(publicCareersApply(careersName, publicKey), fd);
            if (body?.error) throw new Error(body.message);
            onDone({ message: body.message, ...body.data });
        } catch (err) {
            const status = err?.response?.status;
            setError(status === 404 ? 'This role is no longer open.'
                : status === 429 ? 'Too many attempts. Please try again in a few minutes.'
                    : status === 400 || status === 409 ? msgOf(err, 'Please check your details.')
                        : 'We could not submit your application. Please try again.');
        } finally {
            setSending(false);
        }
    };

    const base = { size: 'small', fullWidth: true, slotProps: { inputLabel: { shrink: true } }, sx: field };

    return (
        <Box component="form" onSubmit={submit} noValidate sx={{ p: { xs: 2.2, sm: 3 }, bgcolor: '#fff', border: '1px solid #E6EAF1', borderRadius: '12px' }}>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0F172A', mb: 2 }}>Apply for this role</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField {...base} required label="Full name" value={form.fullName} onChange={set('fullName')}
                        error={tried && nameBad} helperText="Up to 120 characters" slotProps={{ ...base.slotProps, htmlInput: { maxLength: 120 } }} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField {...base} required label="Email" type="email" value={form.email} onChange={set('email')}
                        error={tried && emailBad} helperText={tried && emailBad ? 'Enter a valid email address' : 'We will use this to contact you'}
                        slotProps={{ ...base.slotProps, htmlInput: { maxLength: 120 } }} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField {...base} required label="Phone" value={form.phone} onChange={set('phone')} error={tried && phoneBad}
                        helperText="10 to 15 digits" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField {...base} label="Current company" value={form.currentCompany} onChange={set('currentCompany')}
                        helperText="Leave blank if you are a fresher" slotProps={{ ...base.slotProps, htmlInput: { maxLength: 120 } }} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField {...base} label="Experience (years)" type="number" value={form.experienceYears} onChange={set('experienceYears')}
                        error={tried && expBad} helperText="0 to 60, e.g. 2.5" slotProps={{ ...base.slotProps, htmlInput: { min: 0, max: 60, step: 0.1 } }} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField {...base} label="Expected salary (per year)" type="number" value={form.expectedSalary} onChange={set('expectedSalary')}
                        error={tried && salaryBad} helperText={salaryBad ? 'Cannot be negative' : ' '} slotProps={{ ...base.slotProps, htmlInput: { min: 0 } }} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField {...base} label="Notice period" value={form.noticePeriod} onChange={set('noticePeriod')}
                        error={tried && noticeBad} helperText="Keep under 30 characters, e.g. 30 days" />
                </Grid>
                <Grid size={12}>
                    <Button component="label" startIcon={<UploadFileRoundedIcon />} sx={{ ...ghostBtn, height: 40, px: 2 }}>
                        {resume ? resume.name : 'Attach résumé *'}
                        <input hidden type="file" accept=".pdf,.doc,.docx" onChange={pick} />
                    </Button>
                    <Typography sx={{ fontSize: 12, mt: 0.6, color: resumeErr || (tried && resumeMissing) ? '#E11D48' : '#98A0AE' }}>
                        {resumeErr || (tried && resumeMissing ? 'A résumé is required (.pdf, .doc or .docx, up to 10 MB).' : 'Required. .pdf, .doc or .docx, up to 10 MB')}
                    </Typography>
                </Grid>
            </Grid>

            {/* Honeypot: hidden from people, never labelled or filled in, never sent. */}
            <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }} />

            <Button type="submit" disabled={sending} startIcon={sending ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
                sx={{ ...solidBtn, mt: 3, height: 44, px: 3.4 }}>
                {sending ? 'Submitting…' : 'Submit application'}
            </Button>
        </Box>
    );
}

export function CareersTrack() {
    const { careersName } = useParams();
    const [code, setCode] = useState('');
    const [email, setEmail] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [apps, setApps] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim() || busy) return;
        setBusy(true); setError(''); setApps(null);
        try {
            const payload = { email: email.trim() };
            if (code.trim()) payload.trackingCode = code.trim();
            const { data: body } = await axios.post(publicCareersTrack(careersName), payload);
            if (body?.error) throw new Error(body.message);
            const d = body.data || {};
            setApps(Array.isArray(d.applications) ? d.applications : (d.role ? [d] : []));
        } catch (err) {
            const s = err?.response?.status;
            setError(s === 404 ? 'No application found for these details.'
                : s === 429 ? 'Too many attempts. Please try again in a few minutes.'
                    : msgOf(err, 'We could not check your application. Please try again.'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Shell careersName={careersName}>
            <Typography sx={{ fontSize: { xs: 22, sm: 28 }, fontWeight: 800, color: '#0F172A', mb: 2 }}>Track your application</Typography>
            <Box component="form" onSubmit={submit} sx={{ p: { xs: 2.2, sm: 3 }, bgcolor: '#fff', border: '1px solid #E6EAF1', borderRadius: '12px', maxWidth: 520 }}>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <Stack spacing={2}>
                    <TextField size="small" required label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                        helperText="The email you applied with" slotProps={{ inputLabel: { shrink: true } }} sx={field} />
                    <TextField size="small" label="Application ID (optional)" placeholder="ARA-2026-01" value={code} onChange={(e) => setCode(e.target.value)}
                        helperText="Leave blank to see all your applications" slotProps={{ inputLabel: { shrink: true } }} sx={field} />
                    <Button type="submit" disabled={busy || !email.trim()}
                        startIcon={busy ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null} sx={{ ...solidBtn, height: 42 }}>
                        {busy ? 'Checking…' : 'Check status'}
                    </Button>
                </Stack>
            </Box>

            {apps && apps.map((a, n) => {
                const rows = [
                    ['Application ID', a.applicationId], ['Role', a.role], ['Applied on', a.appliedOn], ['Status', a.status], ['Last updated', a.lastUpdated],
                    ...(a.interview ? [['Interview', `${a.interview.date}, ${a.interview.time} (${a.interview.mode})`]] : []),
                    ...(a.rejectReason ? [['Reason', a.rejectReason]] : []),
                    ...(a.nextStep?.canApplyAgainOn ? [['You can apply again on', a.nextStep.canApplyAgainOn]] : []),
                ].filter(([, v]) => v);
                return (
                    <Box key={a.applicationId || n} sx={{ mt: 2.5, p: 2.4, bgcolor: '#fff', border: `1px solid ${PRIMARY_BORDER}`, borderRadius: '12px', maxWidth: 520 }}>
                        {rows.map(([l, val], i) => (
                            <Stack key={l} direction="row" sx={{ justifyContent: 'space-between', gap: 2, py: 1, borderTop: i ? '1px solid #F1F0F9' : 'none' }}>
                                <Typography sx={{ fontSize: 13, color: '#98A0AE' }}>{l}</Typography>
                                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#334155', textAlign: 'right' }}>{val}</Typography>
                            </Stack>
                        ))}
                    </Box>
                );
            })}
        </Shell>
    );
}
